import { AIAnalysisRequest, AIAnalysisResponse, VoiceProcessingResult } from '../types/phase2'
import { analyzeVoice } from './elevenlabs'

// Sentiment analysis using multiple approaches
export const analyzeSentiment = (text: string): {
  score: number
  label: 'positive' | 'neutral' | 'negative'
  confidence: number
} => {
  // Simple keyword-based sentiment analysis
  const positiveWords = ['good', 'great', 'excellent', 'happy', 'satisfied', 'motivated', 'excited', 'confident']
  const negativeWords = ['bad', 'terrible', 'awful', 'sad', 'frustrated', 'stressed', 'overwhelmed', 'anxious']
  
  const words = text.toLowerCase().split(/\s+/)
  let positiveCount = 0
  let negativeCount = 0
  
  words.forEach(word => {
    if (positiveWords.some(pw => word.includes(pw))) positiveCount++
    if (negativeWords.some(nw => word.includes(nw))) negativeCount++
  })
  
  const totalSentimentWords = positiveCount + negativeCount
  if (totalSentimentWords === 0) {
    return { score: 0.5, label: 'neutral', confidence: 0.3 }
  }
  
  const score = positiveCount / totalSentimentWords
  const confidence = Math.min(totalSentimentWords / words.length * 5, 1)
  
  let label: 'positive' | 'neutral' | 'negative'
  if (score > 0.6) label = 'positive'
  else if (score < 0.4) label = 'negative'
  else label = 'neutral'
  
  return { score, label, confidence }
}

// Stress level detection from text patterns
export const analyzeStressLevel = (text: string): number => {
  const stressIndicators = [
    'overwhelmed', 'stressed', 'pressure', 'deadline', 'urgent', 'crisis',
    'exhausted', 'burned out', 'too much', 'can\'t handle', 'breaking point'
  ]
  
  const words = text.toLowerCase()
  let stressScore = 0
  
  stressIndicators.forEach(indicator => {
    if (words.includes(indicator)) {
      stressScore += 0.2
    }
  })
  
  // Check for urgency patterns
  if (words.includes('need to') || words.includes('have to') || words.includes('must')) {
    stressScore += 0.1
  }
  
  // Check for time pressure
  if (words.includes('quickly') || words.includes('asap') || words.includes('immediately')) {
    stressScore += 0.15
  }
  
  return Math.min(stressScore, 1)
}

// Engagement level analysis
export const analyzeEngagement = (text: string): number => {
  const engagementIndicators = [
    'excited', 'motivated', 'passionate', 'love', 'enjoy', 'interested',
    'challenging', 'learning', 'growing', 'opportunity', 'goal', 'achieve'
  ]
  
  const disengagementIndicators = [
    'boring', 'repetitive', 'meaningless', 'waste', 'pointless', 'uninterested',
    'going through motions', 'clock watching', 'dreading', 'unmotivated'
  ]
  
  const words = text.toLowerCase()
  let engagementScore = 0.5 // Start neutral
  
  engagementIndicators.forEach(indicator => {
    if (words.includes(indicator)) {
      engagementScore += 0.1
    }
  })
  
  disengagementIndicators.forEach(indicator => {
    if (words.includes(indicator)) {
      engagementScore -= 0.1
    }
  })
  
  return Math.max(0, Math.min(1, engagementScore))
}

// Burnout risk assessment
export const analyzeBurnoutRisk = (text: string, recentAssessments: any[] = []): number => {
  let riskScore = 0
  
  // Text-based indicators
  const burnoutKeywords = [
    'exhausted', 'drained', 'burned out', 'can\'t cope', 'giving up',
    'no energy', 'empty', 'cynical', 'detached', 'meaningless'
  ]
  
  const words = text.toLowerCase()
  burnoutKeywords.forEach(keyword => {
    if (words.includes(keyword)) {
      riskScore += 0.15
    }
  })
  
  // Historical pattern analysis
  if (recentAssessments.length >= 3) {
    const recentScores = recentAssessments.slice(-3).map(a => a.sentiment_score)
    const averageScore = recentScores.reduce((a, b) => a + b, 0) / recentScores.length
    
    if (averageScore < 0.4) {
      riskScore += 0.3 // Consistently low sentiment
    }
    
    // Check for declining trend
    const isDecreasing = recentScores.every((score, i) => 
      i === 0 || score <= recentScores[i - 1]
    )
    
    if (isDecreasing) {
      riskScore += 0.2
    }
  }
  
  return Math.min(riskScore, 1)
}

// Generate personalized recommendations
export const generateRecommendations = (analysis: {
  sentiment_score: number
  stress_level: number
  engagement_level: number
  burnout_risk: number
  key_themes: string[]
}): string[] => {
  const recommendations: string[] = []
  
  // Stress-based recommendations
  if (analysis.stress_level > 0.6) {
    recommendations.push('Consider taking short breaks throughout the day to manage stress levels')
    recommendations.push('Practice deep breathing exercises when feeling overwhelmed')
    recommendations.push('Speak with your manager about workload distribution')
  }
  
  // Engagement-based recommendations
  if (analysis.engagement_level < 0.4) {
    recommendations.push('Explore new learning opportunities or skill development')
    recommendations.push('Discuss career growth opportunities with your supervisor')
    recommendations.push('Consider taking on a new project that aligns with your interests')
  }
  
  // Burnout prevention
  if (analysis.burnout_risk > 0.5) {
    recommendations.push('Schedule time for activities you enjoy outside of work')
    recommendations.push('Consider speaking with a mental health professional')
    recommendations.push('Evaluate your work-life balance and make necessary adjustments')
  }
  
  // Sentiment-based recommendations
  if (analysis.sentiment_score < 0.4) {
    recommendations.push('Connect with colleagues or friends for social support')
    recommendations.push('Practice gratitude by noting three positive things each day')
    recommendations.push('Consider mindfulness or meditation practices')
  }
  
  // Theme-based recommendations
  if (analysis.key_themes.includes('workload')) {
    recommendations.push('Break large tasks into smaller, manageable steps')
    recommendations.push('Use time-blocking techniques to organize your day')
  }
  
  if (analysis.key_themes.includes('relationships')) {
    recommendations.push('Schedule regular check-ins with team members')
    recommendations.push('Practice active listening in conversations')
  }
  
  return recommendations.slice(0, 5) // Limit to top 5 recommendations
}

// Main AI analysis function
export const performAIAnalysis = async (request: AIAnalysisRequest): Promise<AIAnalysisResponse> => {
  const startTime = Date.now()
  
  try {
    let text = request.text || ''
    let voiceAnalysis: any = null
    
    // Process voice if audio URL provided
    if (request.audio_url) {
      // In a real implementation, this would fetch and process the audio
      voiceAnalysis = {
        transcript: "Sample transcript from voice analysis",
        stress_indicators: {
          voice_tremor: 0.3,
          speech_rate: 1.1,
          pause_frequency: 0.7,
          emotional_valence: 0.4,
          confidence_level: 0.8
        }
      }
      text = voiceAnalysis.transcript
    }
    
    // Perform text analysis
    const sentiment = analyzeSentiment(text)
    const stressLevel = analyzeStressLevel(text)
    const engagementLevel = analyzeEngagement(text)
    const burnoutRisk = analyzeBurnoutRisk(text, []) // Would pass actual assessment history
    
    // Extract key themes (simplified)
    const keyThemes = extractKeyThemes(text)
    
    // Generate analysis results
    const results = {
      sentiment_score: sentiment.score,
      stress_level: stressLevel,
      engagement_level: engagementLevel,
      burnout_risk: burnoutRisk,
      key_themes: keyThemes,
      emotional_state: determineEmotionalState(sentiment.score, stressLevel, engagementLevel),
      recommendations: generateRecommendations({
        sentiment_score: sentiment.score,
        stress_level: stressLevel,
        engagement_level: engagementLevel,
        burnout_risk: burnoutRisk,
        key_themes: keyThemes
      })
    }
    
    const processingTime = Date.now() - startTime
    
    return {
      analysis_id: `analysis_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      results,
      confidence_scores: {
        overall: (sentiment.confidence + 0.8 + 0.7 + 0.6) / 4, // Average confidence
        sentiment: sentiment.confidence,
        stress: 0.8,
        engagement: 0.7,
      },
      processing_time_ms: processingTime
    }
  } catch (error) {
    console.error('AI Analysis Error:', error)
    throw new Error('Failed to perform AI analysis')
  }
}

// Helper functions
const extractKeyThemes = (text: string): string[] => {
  const themes: string[] = []
  const words = text.toLowerCase()
  
  if (words.includes('work') || words.includes('task') || words.includes('project')) {
    themes.push('workload')
  }
  if (words.includes('team') || words.includes('colleague') || words.includes('manager')) {
    themes.push('relationships')
  }
  if (words.includes('time') || words.includes('deadline') || words.includes('schedule')) {
    themes.push('time_management')
  }
  if (words.includes('learn') || words.includes('grow') || words.includes('develop')) {
    themes.push('development')
  }
  if (words.includes('balance') || words.includes('personal') || words.includes('family')) {
    themes.push('work_life_balance')
  }
  
  return themes
}

const determineEmotionalState = (sentiment: number, stress: number, engagement: number): string => {
  if (sentiment > 0.7 && stress < 0.3 && engagement > 0.6) {
    return 'thriving'
  } else if (sentiment > 0.5 && stress < 0.5 && engagement > 0.5) {
    return 'positive'
  } else if (sentiment < 0.4 || stress > 0.6 || engagement < 0.3) {
    return 'struggling'
  } else {
    return 'neutral'
  }
}